/**
 * Prepare loading indicator object
 * @param loading {Object}
 * @returns {{id:string,message:string,timeout:int,timer:int,visible:boolean}}
 */
const makeLoading = (loading) => {
    loading.id = loading.id || randomId();
    loading.message = loading.message || null;
    loading.timeout = loading.timeout || 0;
    loading.timer = 0;
    loading.visible = false;
    return loading;
};

/**
 * Prepare alert object
 * @param alert {Object}
 * @returns {{id:string,type:string,message:string,timeout:int,visible:boolean}}
 */
const makeAlert = (alert) => {
    alert.id = alert.id || randomId();
    alert.type = alert.type || 'info';
    alert.message = alert.message || null;
    alert.timeout = alert.timeout || 0;
    alert.timer = 0;
    alert.visible = false;
    return alert;
};

/**
 * Generate random id
 * @returns {string}
 */
const randomId = () => {
    return (new Date()).getTime().toString(36)
        + Math.random().toString(36);
};

/**
 * Schedule object removal
 * @param object {Object}
 * @returns {Promise<*>}
 */
const schedule = (object) => {
    return new Promise((resolve, reject) => {
        if (object.timeout) {
            object.timer = setTimeout(() => resolve(object), object.timeout);
        } else {
            reject(object);
        }
    });
};

/**
 * Show object
 * @param object {Object}
 * @returns {*}
 */
const show = (object) => {
    object.visible = true;
    return object;
}

/**
 * Hide object and remove timer if set
 * @param object {Object}
 * @returns {*}
 */
const hide = (object) => {
    object.visible = false;
    if (object.timer) {
        clearTimeout(object.timer);
    }
    return object;
};

export default {
    namespaced: true,
    state: {
        loading: [],
        alerts: [],
    },
    getters: {
        getLoadingMessages: state => state.loading.reduce(
            (messages, loading) => {
                if (
                    loading.message
                    && messages.indexOf(loading.message) === -1
                ) {
                    messages.push(loading.message);
                }
                return messages;
            },
            []
        ),
        getLoadingVisible: state => !!state.loading.length,
        getAlerts: state => state.alerts,
    },
    mutations: {
        addLoading: (state, loading) => state.loading.push(show(loading)),
        removeLoading: (state, id) => {
            const index = state.loading.findIndex(item => item.id === id);
            if (index > -1) {
                const removed = state.loading.splice(index, 1);
                removed.forEach(hide);
            }
        },
        removeAllLoading: state => {
            while (state.loading.length) {
                hide(state.loading.pop());
            }
        },
        addAlert: (state, alert) => state.alerts.push(show(alert)),
        removeAlert: (state, id) => {
            const index = state.alerts.findIndex(item => item.id === id);
            if (index > -1) {
                const removed = state.alerts.splice(index, 1);
                removed.forEach(hide);
            }
        },
        removeAllAlerts: state => {
            while (state.alerts.length) {
                hide(state.alerts.pop());
            }
        }
    },
    actions: {
        async addLoading(context, config) {
            const loading = makeLoading(config);
            schedule(loading)
                .then(() => context.commit('removeLoading', loading.id))
                .catch(() => false);
            context.commit('addLoading', loading);
            return {
                id: loading.id,
                hide() {
                    return context.dispatch('removeLoading', loading.id);
                }
            };
        },
        async removeLoading(context, idOrObject) {
            const id = typeof idOrObject === 'object' ? idOrObject.id : idOrObject;
            context.commit('removeLoading', id);
        },
        async removeAllLoading(context) {
            context.commit('removeAllLoading');
        },
        async addAlert(context, config) {
            const alert = makeAlert(config);
            alert.hide = () => {
                return context.dispatch('removeAlert', alert.id);
            };
            schedule(alert)
                .then(() => context.commit('removeAlert', alert.id))
                .catch(() => false);
            context.commit('addAlert', alert);
            return {
                id: alert.id,
                hide: alert.hide,
            };
        },
        async removeAlert(context, idOrObject) {
            const id = typeof idOrObject === 'object' ? idOrObject.id : idOrObject;
            context.commit('removeAlert', id);
        },
        async removeAllAlerts(context) {
            context.commit('removeAllAlerts');
        },
    }
}